import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@rti/shared";
import { useAuth } from "@/contexts/AuthContext";
import type { UserActivityResponse } from "@rti/shared/src/types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const UserActivity: React.FC = () => {
  const { user } = useAuth();
  const { email } = useParams<{ email: string }>();
  const [data, setData] = useState<UserActivityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Pagination and page size states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Date selector state
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  useEffect(() => {
    if (!user?.is_admin) {
      // Redirect non-admin users to dashboard
      window.location.href = '/dashboard';
      return;
    }

    const fetchActivity = async () => {
      if (!email) return;
      setLoading(true);
      try {
        const res: UserActivityResponse = await apiClient.getUserActivity(email);
        setData(res);
      } catch (error: any) {
        setData({ email: email || "", activities: [], error: "Failed to fetch user activity." });
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [email]);

  const renderMetadata = (metadata: any) => {
    if (!metadata || (typeof metadata === "object" && Object.keys(metadata).length === 0)) {
      return null;
    }
    let parsedMetadata = metadata;
    if (typeof metadata === "string") {
      try {
        parsedMetadata = JSON.parse(metadata);
      } catch {
        // If parsing fails, treat as raw string
        return <pre className="whitespace-pre-wrap">{metadata}</pre>;
      }
    }
    if (typeof parsedMetadata === "object" && Object.keys(parsedMetadata).length === 0) {
      return null;
    }
    if (typeof parsedMetadata === "object") {
      return (
        <ul className="list-disc list-inside">
          {Object.entries(parsedMetadata).map(([key, value]) => {
            let displayValue = value;
            if (key.toLowerCase() === "date" && typeof value === "string") {
              // Try to detect ISO format or similar (e.g., "2025-08-01")
              const parsedDate = new Date(value);
              if (!isNaN(parsedDate.getTime())) {
                displayValue = parsedDate.toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                  timeZone: "UTC",
                });
              }
            }
            return (
              <p key={key} className="point-style-none">
                <span className="text-rs-light-purple-2">
                  {key.toLowerCase().charAt(0).toUpperCase() + key.slice(1)}:
                </span>{" "}
                {typeof displayValue === "object" ? JSON.stringify(displayValue) : String(displayValue)}
              </p>
            );
          })}
        </ul>
      );
    }
    // fallback for other types
    return <pre className="whitespace-pre-wrap">{String(parsedMetadata)}</pre>;
  };

  // Normalize any timestamp (string or number) to YYYY-MM-DD in local timezone
  const dateOnly = (ts: string | number): string => {
    const d = typeof ts === 'number' ? new Date(ts) : new Date(ts);
    if (isNaN(d.getTime())) return '';
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  };

  // Sort state and logic
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Helper for sorting
  const getComparator = (key: string) => {
    return (a: any, b: any) => {
      let aVal = a[key];
      let bVal = b[key];
      if (key === "timestamp") {
        const toMs = (v: any) => (typeof v === "number" ? v : new Date(v).getTime());
        aVal = toMs(aVal);
        bVal = toMs(bVal);
      } else if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return -1;
      if (aVal > bVal) return 1;
      return 0;
    };
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        // Toggle direction
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  // Apply sorting to activities
  let sortedActivities = data?.activities || [];
  if (sortConfig && sortedActivities.length > 0) {
    sortedActivities = [...sortedActivities].sort(getComparator(sortConfig.key));
    if (sortConfig.direction === "desc") {
      sortedActivities.reverse();
    }
  }

  // Filter by selected date if any
  if (selectedDate) {
    const selectedDateStr = selectedDate.toISOString().slice(0, 10);
    sortedActivities = sortedActivities.filter(a => dateOnly(a.timestamp) === selectedDateStr);
  }

  // Pagination calculations
  const totalItems = sortedActivities.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginatedActivities = sortedActivities.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Utility for showing arrow
  const sortArrow = (col: string) => {
    if (!sortConfig || sortConfig.key !== col) return null;
    return sortConfig.direction === "asc" ? <span> ↑</span> : <span> ↓</span>;
  };

  // Extract unique available dates from data.activities for datepicker
  const availableDatesSet = new Set<string>();
  data?.activities.forEach(a => {
    if (a.timestamp) {
      const dOnly = dateOnly(a.timestamp);
      if (dOnly) availableDatesSet.add(dOnly);
    }
  });
  const availableDates = Array.from(availableDatesSet).map(d => {
    const [year, month, day] = d.split('-').map(Number);
    return new Date(year, month - 1, day);
  });

  // Disable dates not in availableDates
  const isDateAvailable = (date: Date) => {
    return availableDates.some(d => d.getTime() === date.getTime());
  };

  return (
    <div className="bg-rs-off-black text-rs-off-white min-h-screen p-8">
      <button className="jet mb-4" onClick={() => navigate("/user-management")}>
        ← Back to Users
      </button>
      <h1 className="text-2xl mb-4">User Activity for {email}</h1>
      {loading ? (
        <div>Loading activity...</div>
      ) : data?.error ? (
        <div className="text-red-400 mb-8">Error fetching activity: {data.error}</div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="datePicker" className="text-rs-dark-green">Select Date:</label>
              <DatePicker
                id="datePicker"
                selected={selectedDate}
                onChange={(date) => {
                  setSelectedDate(date);
                  setCurrentPage(1);
                }}
                filterDate={isDateAvailable}
                placeholderText="Select a date"
                className="bg-rs-off-black border border-rs-off-white/20 text-rs-off-white px-2 py-1 rounded"
                dateFormat="yyyy-MM-dd"
                isClearable
              />
            </div>
            <div className="flex items-center gap-2">
              <label htmlFor="itemsPerPage" className="text-rs-dark-green">Items per page:</label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-rs-off-black border border-rs-off-white/20 text-rs-off-white px-2 py-1 rounded"
              >
                {[25, 50, 100, 250].map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>
          <table className="min-w-full border border-rs-off-white/20">
            <thead>
              <tr className="bg-rs-jet/50">
                <th className="p-2 text-left cursor-pointer select-none" onClick={() => handleSort('timestamp')}>
                  Date{sortArrow('timestamp')}
                </th>
                <th className="p-2 text-left cursor-pointer select-none" onClick={() => handleSort('event')}>
                  Action{sortArrow('event')}
                </th>
                <th className="p-2 text-left cursor-pointer select-none" onClick={() => handleSort('title')}>
                  Title{sortArrow('title')}
                </th>
                <th className="p-2 text-left">Details</th>
                <th className="p-2 text-left cursor-pointer select-none" onClick={() => handleSort('url')}>
                  URL{sortArrow('url')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data && paginatedActivities && paginatedActivities.length > 0 ? (
                paginatedActivities.map((a, idx) => (
                  <tr key={idx} className="border-t border-rs-off-white/20">
                    <td className="p-2">{new Date(a.timestamp).toLocaleString()}</td>
                    <td className="p-2">
                      {a.event
                        ? a.event
                            .split('_')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                        : ""}
                    </td>
                    <td className="p-2">{a.title || "(untitled)"}</td>
                    <td className="p-2 whitespace-pre-wrap">{renderMetadata(a.event_metadata)}</td>
                    <td className="p-2 text-rs-green">
                      <a href={a.url} target="_blank" rel="noreferrer">{a.url}</a>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="p-4 text-center text-rs-off-white/60">No activity found</td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="flex justify-between items-center mt-4 text-rs-off-white/70">
            <button
              className="jet px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>
              Page {currentPage} of {totalPages} ({totalItems} items)
            </span>
            <button
              className="jet px-3 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default UserActivity;